import { useState, useEffect, useCallback } from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

export const useFormChangeDetection = (form: UseFormReturn<FieldValues>) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Handle navigation attempts
  const handleNavigationAttempt = useCallback((targetPath: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(targetPath);
      setShowConfirmationModal(true);
    } else {
      return targetPath;
    }
  }, [hasUnsavedChanges]);

  // Handle confirmation modal actions
  const handleConfirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      setShowConfirmationModal(false);
      setPendingNavigation(null);
      setHasUnsavedChanges(false);
      return pendingNavigation;
    }
  }, [pendingNavigation]);

  const handleCancelNavigation = useCallback(() => {
    setShowConfirmationModal(false);
    setPendingNavigation(null);
  }, []);

  // Reset unsaved changes flag
  const resetUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  // Global navigation interceptor
  const interceptNavigation = useCallback((targetPath: string) => {
    // Check if we're in the campaign creation flow
    const isInCampaignCreation = location.pathname.includes('/campaigns/create');
    
    if (isInCampaignCreation && hasUnsavedChanges) {
      setPendingNavigation(targetPath);
      setShowConfirmationModal(true);
      return false; // Prevent immediate navigation
    }
    return true; // Allow navigation
  }, [location.pathname, hasUnsavedChanges]);

  // Handle navigation with confirmation
  const handleNavigationWithConfirmation = useCallback((targetPath: string) => {
    const result = handleNavigationAttempt(targetPath);
    if (result) {
      navigate(result);
    }
  }, [handleNavigationAttempt, navigate]);

  // Handle confirmation modal actions
  const handleConfirmNavigationAction = useCallback(() => {
    const targetPath = handleConfirmNavigation();
    if (targetPath) {
      navigate(targetPath);
    }
  }, [handleConfirmNavigation, navigate]);

  return {
    hasUnsavedChanges,
    showConfirmationModal,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation,
    resetUnsavedChanges,
    interceptNavigation,
    handleNavigationWithConfirmation,
    handleConfirmNavigationAction,
  };
}; 